/**
 * consent.js — single source of truth for analytics/tracking consent.
 *
 * Batch 2 §1: PostHog and any other third-party analytics must NOT fire
 * before the visitor has explicitly opted in. Session recording stays off
 * regardless of consent state (recording keystrokes is materially different
 * from counting pageviews and would need a separate opt-in).
 *
 * The consent value is persisted in localStorage under CONSENT_KEY and
 * broadcast to listeners via a CustomEvent so any late-loading component
 * can react without reloading the page.
 */

export const CONSENT_KEY = "jj_consent";
export const CONSENT_EVENT = "jj-consent-change";

// Values: "accepted" | "rejected" | null (undecided)
export function getConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.debug("consent: localStorage read blocked", err);
    return null;
  }
}

export function setConsent(value) {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch (err) {
    // Private browsing / storage disabled — the choice is still broadcast in-memory
    // via the CustomEvent below so the current session respects it.
    if (process.env.NODE_ENV !== "production") console.debug("consent: localStorage write blocked", err);
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
}

export function hasAccepted() {
  return getConsent() === "accepted";
}

/**
 * Opt PostHog fully out of tracking. Used on page load until consent is
 * given, and after "Reject" or a "Change preferences" opt-out.
 */
export function disablePostHog() {
  try {
    if (window.posthog && typeof window.posthog.opt_out_capturing === "function") {
      window.posthog.opt_out_capturing();
    }
    // Also make sure session recording is off
    if (window.posthog && typeof window.posthog.stopSessionRecording === "function") {
      window.posthog.stopSessionRecording();
    }
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.debug("consent: disablePostHog failed", err);
  }
}

/**
 * Opt PostHog back in when consent is granted. Session recording is
 * deliberately NOT re-enabled here — that would be a separate opt-in.
 */
export function enablePostHog() {
  try {
    if (window.posthog && typeof window.posthog.opt_in_capturing === "function") {
      window.posthog.opt_in_capturing();
      // Autocapture / pageview were disabled at init while consent was pending.
      // Turn them back on now that we've been given permission.
      if (typeof window.posthog.set_config === "function") {
        window.posthog.set_config({
          capture_pageview: true,
          autocapture: true,
        });
      }
      // Emit an explicit pageview for the current URL (auto capture won't
      // retroactively fire for the current page).
      if (typeof window.posthog.capture === "function") {
        window.posthog.capture("$pageview");
      }
    }
    // Explicitly keep session recording off.
    if (window.posthog && typeof window.posthog.stopSessionRecording === "function") {
      window.posthog.stopSessionRecording();
    }
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.debug("consent: enablePostHog failed", err);
  }
}
